import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { setRecurringSchedule, removeRecurringSchedule } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { useToast } from '@/contexts/ToastContext';
import type { RecurringConfig } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarClock, Pencil, Trash2 } from 'lucide-react';

interface RecurringScheduleCardProps {
  invoiceId: string;
  recurringConfig: RecurringConfig | null;
  onUpdate: () => void;
}

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
] as const;

function frequencyLabel(frequency: string): string {
  return FREQUENCY_OPTIONS.find((o) => o.value === frequency)?.label ?? frequency;
}

export function RecurringScheduleCard({
  invoiceId,
  recurringConfig,
  onUpdate,
}: RecurringScheduleCardProps) {
  const { addToast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state for the schedule dialog
  const [frequency, setFrequency] = useState<RecurringConfig['frequency']>(
    recurringConfig?.frequency ?? 'monthly',
  );
  const [nextDate, setNextDate] = useState(recurringConfig?.nextDate ?? '');
  const [endDate, setEndDate] = useState(recurringConfig?.endDate ?? '');
  const [autoSend, setAutoSend] = useState(recurringConfig?.autoSend ?? false);

  const openDialog = () => {
    // Reset form to current config values when opening
    setFrequency(recurringConfig?.frequency ?? 'monthly');
    setNextDate(recurringConfig?.nextDate ?? '');
    setEndDate(recurringConfig?.endDate ?? '');
    setAutoSend(recurringConfig?.autoSend ?? false);
    setError(null);
    setDialogOpen(true);
  };

  const setScheduleMutation = useMutation({
    mutationFn: () =>
      setRecurringSchedule(invoiceId, {
        frequency,
        nextDate,
        endDate: endDate || undefined,
        autoSend,
      }),
    onSuccess: () => {
      addToast({ type: 'success', title: 'Recurring schedule saved' });
      setDialogOpen(false);
      setError(null);
      onUpdate();
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to save recurring schedule');
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => removeRecurringSchedule(invoiceId),
    onSuccess: () => {
      addToast({ type: 'success', title: 'Recurring schedule removed' });
      onUpdate();
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: err.message || 'Failed to remove schedule' });
    },
  });

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <CalendarClock className="h-4 w-4" />
            Recurring Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recurringConfig ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-text-tertiary">Frequency</span>
                <span>{frequencyLabel(recurringConfig.frequency)}</span>
                <span className="text-text-tertiary">Next Date</span>
                <span>{formatDate(recurringConfig.nextDate)}</span>
                {recurringConfig.endDate && (
                  <>
                    <span className="text-text-tertiary">End Date</span>
                    <span>{formatDate(recurringConfig.endDate)}</span>
                  </>
                )}
                <span className="text-text-tertiary">Auto-send</span>
                <span>{recurringConfig.autoSend ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={openDialog}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Edit Schedule
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeMutation.mutate()}
                  disabled={removeMutation.isPending}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  {removeMutation.isPending ? 'Removing...' : 'Remove Schedule'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-text-tertiary">No recurring schedule</p>
              <Button variant="outline" size="sm" onClick={openDialog}>
                <CalendarClock className="mr-1.5 h-3.5 w-3.5" />
                Set Up Recurring
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5" />
              {recurringConfig ? 'Edit Recurring Schedule' : 'Set Up Recurring Schedule'}
            </DialogTitle>
            <DialogDescription>
              Configure how often this invoice should recur.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Frequency *</Label>
              <Select value={frequency} onValueChange={(val) => setFrequency(val as RecurringConfig['frequency'])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="next-date">Next Date *</Label>
              <Input
                id="next-date"
                type="date"
                value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <p className="text-xs text-text-tertiary">Leave blank for no end date</p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="auto-send"
                checked={autoSend}
                onCheckedChange={(checked) => setAutoSend(checked)}
              />
              <Label htmlFor="auto-send" className="cursor-pointer">
                Automatically send when generated
              </Label>
            </div>

            {error && <p className="text-sm text-red-400">{error}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => setScheduleMutation.mutate()}
              disabled={!nextDate || setScheduleMutation.isPending}
            >
              {setScheduleMutation.isPending ? 'Saving...' : 'Save Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
